import React, { useEffect, useState } from "react";
import { formatDate } from "../../../Utils/fileUtils";
import { useDispatch, useSelector } from "react-redux";
import adjustPrice from "../../../Utils/AdjustPrice";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Select from "../../Common/FormElements/Select/Select";
import { fetchCommercialOptions } from "../../../REDUX/actions/commercials";
import { updateCommande } from "../../../REDUX/actions/commandes";
import {
  fetchCustomerRow,
  viewQuotePdf,
} from "../../../REDUX/actions/appProjects";
import { openRightModal } from "../../../REDUX/actions/rightModal";
import ProjectModal from "../../AppCustomers/Projects/ProjectModal";
import AppCustomerModal from "../../AppCustomers/AppCustomerModal";
import UpdateDeparture from "../../Departure/UpdateDeparture";
import useCanSeePrices from "../../../hooks/useCanSeePrices";

const TEAL = "#14748e";
const fullName = (user) =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ");

/** A link styled like the storyboard's order links (D3). */
const LinkButton = ({ onClick, children }) => (
  <button
    type="button"
    className="btn btn-link p-0 f14 sBold text-start"
    style={{ color: TEAL }}
    onClick={onClick}
  >
    {children}
  </button>
);

const CommandeDetails = ({ commande }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { userRole } = useSelector((state) => state.userReducer);
  // A store's commercial sees no amount of the order, and doesn't reassign it.
  const canSeePrices = useCanSeePrices();
  const isCommercial = userRole === "commercial";
  // A store's order for a client project ("Passer la commande"): project, quote, commercial.
  const isCustomerOrder = !!commande?.project;
  const [commercialOptions, setCommercialOptions] = useState([]);
  // The sheet's `commande` is a local copy: show the pick right away.
  const [pickedCommercial, setPickedCommercial] = useState(null);

  // "Commercial" = a commercial (api::commercial) of the order's store.
  useEffect(() => {
    if (!isCustomerOrder) return;
    const controller = new AbortController();
    fetchCommercialOptions(commande?.reseller?.id, controller.signal)
      .then(setCommercialOptions)
      .catch((error) => {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      });
    return () => controller.abort();
  }, [isCustomerOrder, commande?.reseller?.id]);

  const openContact = async () => {
    const row = await dispatch(fetchCustomerRow(commande?.client?.id));
    if (row) dispatch(openRightModal(<AppCustomerModal customer={row} />));
  };
  const contactName = fullName(commande?.client) || commande?.particulierName;
  const contactLink = commande?.client ? (
    <LinkButton onClick={openContact}>{contactName}</LinkButton>
  ) : null;

  const links = isCustomerOrder && [
    {
      label: t("Project"),
      value: (
        <LinkButton
          onClick={() =>
            dispatch(openRightModal(<ProjectModal projectId={commande.project.id} />))
          }
        >
          N°{commande.project.number ?? commande.project.id} —{" "}
          {commande.project.name}
        </LinkButton>
      ),
      sub: t("Discussion, Pipeline action, Map"),
    },
    commande?.quote && {
      label: t("Quote"),
      value: (
        <LinkButton onClick={() => dispatch(viewQuotePdf(commande.quote.id, t))}>
          {t("Quote no. {{number}} — {{amount}} incl. VAT", {
            number: commande.quote.id,
            amount: `${adjustPrice((Number(commande.quote.totalAmount) || 0).toFixed(2))} €`,
          })}
        </LinkButton>
      ),
      sub: commande.quote.acceptedAt
        ? t("accepted on {{date}}", {
            date: moment(commande.quote.acceptedAt).format("DD/MM/YYYY"),
          })
        : "",
    },
    commande?.delivery && !isCommercial && {
      label: t("Delivery"),
      value: (
        <LinkButton
          onClick={() =>
            dispatch(openRightModal(<UpdateDeparture id={commande.delivery.id} />))
          }
        >
          {t("Delivery no. {{number}}", {
            number: commande.delivery.number ?? commande.number,
          })}
        </LinkButton>
      ),
      sub: t("Logistics › Delivery"),
    },
    {
      label: t("Commercial"),
      value: (
        <Select
          disabled={isCommercial}
          options={commercialOptions}
          value={
            pickedCommercial ??
            commercialOptions.find((o) => o.code === commande?.salesRep?.id) ??
            (commande?.salesRep
              ? { name: fullName(commande.salesRep), code: commande.salesRep.id }
              : null)
          }
          callback={(e) => {
            if (!e.value?.code) return;
            setPickedCommercial(e.value);
            dispatch(updateCommande(commande.id, { salesRep: e.value.code }));
          }}
        />
      ),
      sub: t("By default: the project's commercial."),
    },
  ];

  const contentTab = [
    {
      label: t("Order Number"),
      value: commande?.number ? `#${commande?.number}` : "-",
    },
    {
      label: t("Order Date"),
      value:
        (commande?.oldCreatedAt
          ? formatDate(commande?.oldCreatedAt, "DD-MM-YYYY")
          : formatDate(commande?.createdAt, "DD-MM-YYYY")) || "-",
    },
    {
      label: t(commande?.credit ? "Credit No" : "Invoice No"),
      value: commande?.facture?.number
        ? `#${commande?.facture?.number}`
        : commande?.avoir?.number
          ? `#${commande?.avoir?.number}`
          : "-",
    },
    {
      label: t(commande?.credit ? "Credit Date" : "Invoice Date"),
      value:
        (commande?.credit ? commande?.avoir?.date : commande?.facture?.date) ||
        "-",
    },
    {
      label: t("Purchase Order No"),
      value: commande?.bon_de_commande?.number
        ? `#${commande?.bon_de_commande?.number}`
        : "-",
    },
    {
      label: t("Purchase Order Date"),
      value: formatDate(commande?.bon_de_commande?.date, "DD-MM-YYYY") || "-",
    },
    {
      label: t("Client"),
      value:
        contactLink || commande?.reseller?.name || commande?.particulierName || "-",
    },
    {
      label:
        userRole === "BusinessBroker" ? t("Commission") : t("Account Manager"),
      value:
        userRole === "BusinessBroker"
          ? commande?.comission
            ? `${commande?.comission} €`
            : "-"
          : commande?.business_affiliate?.user?.firstName ||
              commande?.business_affiliate?.user?.lastName
            ? `${commande?.business_affiliate?.user?.firstName} ${commande?.business_affiliate?.user?.lastName}`
            : "-",
    },
    userRole === "admin" && {
      label: `${t("Commission")} (${
        commande?.particulier ? t("Including Taxes") : t("Excluding Taxes")
      })`,
      value: commande?.comission ? `${commande?.comission?.toFixed(2)} €` : "-",
    },
    {
      label: t("Created by"),
      value: commande?.creator?.firstName || "-",
    },

    {
      label: t("Status"),
      value: commande?.status ? t(`${commande?.status}`) : "-",
    },
    {
      label: t("Payment"),
      value: commande?.payStatus ? t(`${commande?.payStatus}`) : "-",
    },
    canSeePrices && {
      label: t("Amount Excl. Tax"),
      value: commande?.totalHT
        ? `${adjustPrice(commande?.totalHT.toFixed(2).toString())} €`
        : "-",
    },
    canSeePrices && {
      label: t("Amount Incl. Tax"),
      value: commande?.totalTTC
        ? `${adjustPrice(commande?.totalTTC.toFixed(2).toString())} €`
        : "-",
    },
    canSeePrices && {
      label: t("VAT"),
      value: commande?.TVA
        ? `${adjustPrice(commande?.TVA.toFixed(2).toString())} €`
        : "-",
    },
    userRole === "admin" && {
      label: t("Margin"),
      value: commande?.marge
        ? `${adjustPrice(commande?.marge.toFixed(2).toString())} €`
        : "-",
    },
    userRole === "admin" && {
      label: t("Margin Rate"),
      value: commande?.txMarge ? `${commande?.txMarge?.toFixed(2)} %` : "-",
    },
    canSeePrices && {
      label: t("Delivery Fees Excl. Tax"),
      value: commande?.totalDeliveryHT
        ? `${adjustPrice(commande?.totalDeliveryHT.toFixed(2).toString())} €`
        : "-",
    },
    canSeePrices && {
      label: t("Delivery Fees Including Tax"),
      value: commande?.totalDeliveryTTC
        ? `${adjustPrice(commande?.totalDeliveryTTC.toFixed(2).toString())} €`
        : "-",
    },
    {
      label: t("Delivery Method"),

      value: commande?.delivery_type?.name
        ? commande?.delivery_type?.name
        : "-",
    },
    {
      label: t("Estimated Delivery Date"),
      value: commande?.estimatedDeliveryDate || "-",
    },
    {
      label: t("Actual Delivery Date"),
      value: commande?.realDeliveryDate || "-",
    },
    {
      label: t("Delivery Address"),
      value: commande?.addresseDeLivraison
        ? `${commande?.addresseDeLivraison?.street}, </br>${commande?.addresseDeLivraison?.zip} ${commande?.addresseDeLivraison?.city?.name},</br> ${commande?.addresseDeLivraison?.country?.name}`
        : "-",
    },
    {
      label: t("Billing Address"),
      value: commande?.addresseDeFacturation
        ? `${commande?.addresseDeFacturation?.street}, </br>${commande?.addresseDeFacturation?.zip} ${commande?.addresseDeFacturation?.city?.name}, </br>${commande?.addresseDeFacturation?.country?.name}`
        : "-",
    },
    {
      label: t("Customer Service Order"),
      value: commande?.free ? t("Yes") : t("No"),
    },
    {
      label: t("Individual Order"),
      value: commande?.particulier ? t("Yes") : t("No"),
    },
    commande?.particulier && {
      label: t("Individual Name"),
      value: contactLink || commande?.particulierName || "-",
    },
    commande?.particulier && {
      label: t("Individual Email"),
      value: commande?.client?.email || commande?.particulierEmail || "-",
    },
    commande?.particulier && {
      label: t("Individual Company"),
      value: commande?.particulierCompany || "-",
    },
    commande?.particulier && {
      label: t("Individual Phone No"),
      value: commande?.client?.phoneNumber || commande?.particulierPhone || "-",
    },

    {
      label: t("Major account order"),
      value: commande?.majorAccountOrder ? t("Yes") : t("No"),
    },

    // commande?.majorAccountOrder && {
    //   label: t("Major account Name"),
    //   value: commande?.majorAccountName || "-",
    // },
    // commande?.majorAccountOrder && {
    //   label: t("Major account Email"),
    //   value: commande?.majorAccountEmail || "-",
    // },

    // commande?.majorAccountOrder && {
    //   label: t("Major account Phone No"),
    //   value: commande?.majorAccountPhone || "-",
    // },
  ];

  const cell = (item, index) => (
    <div className="my-3 col-3" key={index}>
      <p className="f14 bold text-ultraBlack mb-2">{item?.label}</p>
      {typeof item?.value === "string" ? (
        <p
          className={`f14 text-ultraBlack mb-2`}
          dangerouslySetInnerHTML={{ __html: item?.value }}
        ></p>
      ) : (
        <div className="f14 text-ultraBlack mb-2">{item?.value}</div>
      )}
      {item?.sub && <p className="f12 text-brightGrey mb-0">{item.sub}</p>}
    </div>
  );

  return (
    <>
      {links && (
        <div className="mt-3 p-3 br10" style={{ background: "#f4f9fa" }}>
          <p className="f14 bold text-blue mb-0">{t("Client project links")}</p>
          <div className="d-flex align-items-start flex-wrap">
            {links.filter(Boolean).map(cell)}
          </div>
        </div>
      )}
      <div className="d-flex  my-3  align-items-start flex-wrap">
        {contentTab?.filter((item) => item)?.map(cell)}
      </div>
    </>
  );
};

export default CommandeDetails;
